import { z } from "zod";

/**
 * Canonical DataSource / DataSourceLink configuration schemas.
 *
 * These describe the three pieces of config that make an ingest pipeline
 * self-describing, split across the two layers that own them:
 *
 *   - DataSource owns the *connection* — `connectionType` + `connectionConfig`
 *     (how to reach the external system) plus a `credentialId` referencing the
 *     secret in `core_credentials`.
 *   - DataSourceLink owns the *binding of one external dataset → one ingest
 *     table* — `pullConfig` (where/what to pull) and `parseConfig` (how to
 *     interpret it).
 *
 * They live in `@erp/shared-types` so the RPC contracts (`@erp/rpc`), the
 * backend services, and the workflow steps (`@erp/workflows`) all validate
 * against a single source of truth.
 */

// ── Connection config (DataSource: how to connect + authenticate) ──────────

/**
 * Connection *kinds* group connection types into families that steps target
 * generically — e.g. the Blob Storage DataSource Adapter operates on any
 * `blob`-kind connection regardless of provider.
 */
export const DATA_SOURCE_CONNECTION_KINDS = ["blob"] as const;

export const dataSourceConnectionKindSchema = z.enum(
  DATA_SOURCE_CONNECTION_KINDS,
);
export type DataSourceConnectionKind = z.infer<
  typeof dataSourceConnectionKindSchema
>;

/**
 * Azure Blob Storage connection. Non-secret connection params live here; the
 * authoritative connection string is the secret of the linked
 * `azure_blob` credential (`DataSource.credentialId`).
 */
export const azureBlobConnectionConfigSchema = z.object({
  type: z.literal("azure_blob"),
  containerName: z
    .string()
    .min(1)
    .describe("Azure Blob Storage container to read from"),
  // The storage account is not configured here — it is encoded in the
  // connection string of the linked azure_blob credential (single source).
  basePath: z
    .string()
    .optional()
    .describe(
      "Prefix prepended to all blob keys for this connection (e.g. 'inbound/').",
    ),
});
export type AzureBlobConnectionConfig = z.infer<
  typeof azureBlobConnectionConfigSchema
>;

/**
 * AWS S3 connection. Non-secret connection params live here; the access key
 * pair is on the linked `aws` credential (`DataSource.credentialId`) — access
 * key id in the credential metadata, secret access key as the credential
 * secret.
 */
export const awsS3ConnectionConfigSchema = z.object({
  type: z.literal("aws_s3"),
  bucketName: z.string().min(1).describe("S3 bucket to read from"),
  region: z
    .string()
    .min(1)
    .describe("AWS region hosting the bucket (e.g. 'us-east-1')"),
  basePath: z
    .string()
    .optional()
    .describe(
      "Prefix prepended to all object keys for this connection (e.g. 'inbound/').",
    ),
});
export type AwsS3ConnectionConfig = z.infer<typeof awsS3ConnectionConfigSchema>;

/**
 * Standardized definition every DataSource connection type must provide.
 * Adding a new connection type means adding one entry to
 * `DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS` below — the type enum, config
 * union, and kind helpers all derive from it — plus a storage resolver in the
 * backend's `SOURCE_STORAGE_RESOLVERS` registry
 * (`backend/src/workflows/steps/utils/resolveSourceStorage.ts`), which the
 * compiler enforces via the derived type union.
 */
export interface DataSourceConnectionTypeDefinition<
  S extends z.ZodTypeAny = z.ZodTypeAny,
> {
  /** Family the type belongs to; steps/pickers filter by kind, not provider. */
  kind: DataSourceConnectionKind;
  /** UI display name. */
  label: string;
  /** Whether the connection supports reading (source) / writing (sink). */
  capabilities: { pull: boolean; push: boolean };
  /** Non-secret connection params, discriminated by a `type` literal. */
  configSchema: S;
  /** `core_credentials` type the connection authenticates with. */
  credentialType: string;
}

// `capabilities.push` is false for both today: blob connections are
// pull/source-only (the DataSource Writers *read* the source blob). The field
// exists so future types (e.g. an SFTP drop) declare their direction and the
// UI can gate source/sink options.
export const DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS = {
  azure_blob: {
    kind: "blob",
    label: "Azure Blob Storage",
    capabilities: { pull: true, push: false },
    configSchema: azureBlobConnectionConfigSchema,
    credentialType: "azure_blob",
  },
  aws_s3: {
    kind: "blob",
    label: "AWS S3",
    capabilities: { pull: true, push: false },
    configSchema: awsS3ConnectionConfigSchema,
    credentialType: "aws",
  },
} as const satisfies Record<string, DataSourceConnectionTypeDefinition>;

/** Supported DataSource connection protocols (keys of the definitions map). */
export const DATA_SOURCE_CONNECTION_TYPES = ["azure_blob", "aws_s3"] as const;

export const dataSourceConnectionTypeSchema = z.enum(
  DATA_SOURCE_CONNECTION_TYPES,
);
export type DataSourceConnectionType = z.infer<
  typeof dataSourceConnectionTypeSchema
>;

// Compile-time check that DATA_SOURCE_CONNECTION_TYPES and the definitions map
// list exactly the same types (z.enum needs a literal tuple, so the enum can't
// be derived from Object.keys without losing the literal types).
const _assertTypesMatchDefinitions: Record<
  DataSourceConnectionType,
  DataSourceConnectionTypeDefinition
> = DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS;
void _assertTypesMatchDefinitions;

/**
 * Typed connection config, discriminated by `type`. The `DataSource.connectionType`
 * column mirrors `connectionConfig.type` for querying / UI; the config is the
 * source of truth.
 */
export const connectionConfigSchema = z.discriminatedUnion("type", [
  DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS.azure_blob.configSchema,
  DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS.aws_s3.configSchema,
]);
export type ConnectionConfig = z.infer<typeof connectionConfigSchema>;

/** Kind of a connection type, or null when the type is unset/unknown. */
export function connectionKindOf(
  type: DataSourceConnectionType | null | undefined,
): DataSourceConnectionKind | null {
  if (!type) return null;
  return DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS[type].kind;
}

/** All connection types belonging to a kind. */
export function connectionTypesOfKind(
  kind: DataSourceConnectionKind,
): DataSourceConnectionType[] {
  return DATA_SOURCE_CONNECTION_TYPES.filter(
    (type) => DATA_SOURCE_CONNECTION_TYPE_DEFINITIONS[type].kind === kind,
  );
}

// ── Pull config (DataSourceLink: where / what to pull) ─────────────────────

/**
 * How to discover and select the artifact(s) to pull under a connection.
 * Ported from the `blob_storage_fetch` step config so a connection-keyed
 * adapter can resolve the same discovery from the Link instead of per-workflow
 * step config.
 */
export const dataSourceFileSelectionSchema = z.enum([
  "most_recent",
  "oldest",
  "all",
]);
export type DataSourceFileSelection = z.infer<
  typeof dataSourceFileSelectionSchema
>;

export const pullConfigSchema = z.object({
  blobKeyFilterRegex: z
    .string()
    .optional()
    .describe(
      "Regex identifying this link's file(s) among all blob keys under the connection's base path (matched recursively, including subdirectories). Supports date/time placeholders ({YYYY}, {YYYYMM}, {YYYYMMDD}, {HHmm}, {HHmmss}).",
    ),
  fileSelection: dataSourceFileSelectionSchema
    .optional()
    .describe(
      "Which matched file(s) to pull: 'most_recent' or 'oldest' by modified date, or 'all'. Defaults to all matched.",
    ),
  timezone: z
    .string()
    .optional()
    .describe(
      "Timezone used to resolve date/time placeholders in blobKeyFilterRegex (default America/New_York).",
    ),
});
export type PullConfig = z.infer<typeof pullConfigSchema>;

// ── Parse config (DataSourceLink: how to interpret) ────────────────────────

/**
 * One fixed-width field: a destination schema column plus its 0-based character
 * offset and character length in the record. Shared with the FWF workflow steps
 * (re-exported from `@erp/workflows`'s `fwf-shared`).
 */
export const fwfColumnMappingSchema = z.object({
  column: z.string(),
  offset: z.number().int().min(0),
  length: z.number().int().min(1),
});
export type FwfColumnMapping = z.infer<typeof fwfColumnMappingSchema>;

const csvColumnIndexMappingSchema = z.object({
  column: z.string(),
  index: z.number().int().min(0),
});

const csvHeaderMappingSchema = z.object({
  column: z.string(),
  csvHeader: z.string(),
});

/** Delimited (CSV-style) interpretation. */
export const csvParseConfigSchema = z.object({
  format: z.literal("csv"),
  delimiter: z
    .string()
    .min(1)
    .optional()
    .describe("Field delimiter (default: comma)"),
  quoteChar: z
    .string()
    .min(1)
    .optional()
    .describe("Quote character. Leave unset to disable quoting."),
  encoding: z.string().optional().describe("File encoding (default: utf-8)"),
  trimWhitespace: z
    .boolean()
    .optional()
    .describe("Trim leading/trailing whitespace from field values"),
  headerRowIndex: z
    .number()
    .int()
    .min(-1)
    .optional()
    .describe(
      "0-based index of the header row. -1 means the file has no header row.",
    ),
  columnMappingStrategy: z
    .enum(["column_index", "header_mapping"])
    .optional()
    .describe(
      "How source columns map to destination schema fields. 'column_index' maps by position; 'header_mapping' maps a CSV header name to each schema field.",
    ),
  columnIndexMappings: z
    .array(csvColumnIndexMappingSchema)
    .optional()
    .describe(
      "Maps each schema fieldName to its 0-based column index (column_index strategy).",
    ),
  headerMappings: z
    .array(csvHeaderMappingSchema)
    .optional()
    .describe(
      "Maps each schema fieldName to a CSV header name (header_mapping strategy).",
    ),
});
export type CsvParseConfig = z.infer<typeof csvParseConfigSchema>;

/** Fixed-width interpretation. */
export const fwfParseConfigSchema = z.object({
  format: z.literal("fwf"),
  encoding: z.string().optional().describe("File encoding (default: utf-8)"),
  columnMappings: z
    .array(fwfColumnMappingSchema)
    .describe(
      "Ordered field definitions: destination column + 0-based offset + character length.",
    ),
});
export type FwfParseConfig = z.infer<typeof fwfParseConfigSchema>;

/** How to interpret a pulled artifact, discriminated by `format`. */
export const parseConfigSchema = z.discriminatedUnion("format", [
  csvParseConfigSchema,
  fwfParseConfigSchema,
]);
export type ParseConfig = z.infer<typeof parseConfigSchema>;

/** All destination schema columns referenced by a parse config's mappings. */
export function parseConfigMappedColumns(config: ParseConfig): string[] {
  if (config.format === "fwf") {
    return config.columnMappings.map((m) => m.column);
  }
  if (config.columnMappingStrategy === "header_mapping") {
    return (config.headerMappings ?? []).map((m) => m.column);
  }
  return (config.columnIndexMappings ?? []).map((m) => m.column);
}
